import { DashboardData, DashboardKpiKeys, KpiData, PlanPreviousDateRangeDatasetKey } from './types';
import { KpisByPlanChartData } from '../../components/Dashboard/Chart/KpisByPlanChart/types';
import { RP_KPIS } from './constants';
import { MultipleKpisChartData } from '../../components/Dashboard/Chart/MultipleKpisChart/types';
import { SalonKpiValues } from '../KpiResults/types';
import { DateRangeState } from '../../components/Dashboard/DateRangeControl/useDateRange';

export const getMultipleKpisChartData = (data: DashboardData) => {
  if (!data || Object.keys(data).length === 0) {
    return [];
  }

  const filteredDataFields: Partial<Record<DashboardKpiKeys, KpiData>> = {};
  const fields = [
    'spRetention',
    'existingGuestRetention',
    'newGuestRetention',
    'pColorTransactions',
    'pRetailSales',
    'pGuestsPrebooked',
    'pGuestsPurchasing',
    'pTreatmentTransactions',
    'rpct',
    'rprt',
    'tnTransactionsBmrk',
  ] as const;

  fields.forEach((field) => {
    if (data[field]) {
      filteredDataFields[field] = data[field];
    }
  });

  const chartsData: Array<MultipleKpisChartData> = [];

  Object.entries(filteredDataFields).forEach(([field, value]) => {
    const rawValueObject = value.presentDateRange.value.raw;
    const normalizedValue = value.presentDateRange.value.normalized?.value;

    if (!normalizedValue || !rawValueObject) return;

    chartsData.push({
      field: field,
      value: normalizedValue,
      rawValue:
        field === 'tnTransactionsBmrk'
          ? SalonKpiValues[rawValueObject.value as unknown as keyof typeof SalonKpiValues]
          : rawValueObject.value,
      prefix: rawValueObject.prefix || '',
      suffix: rawValueObject.suffix || '',
      benchmarkMin: value.presentDateRange.benchmark.raw.min,
      benchmarkMax: value.presentDateRange.benchmark.raw.max,
    });
  });

  chartsData.sort((a, b) => b.value - a.value);

  return chartsData;
};

export const getPercentKpisByPlanType = ({
  kpiPlanType,
  data,
  planPreviousDateRange,
}: {
  data: DashboardData;
  kpiPlanType: string;
  planPreviousDateRange: PlanPreviousDateRangeDatasetKey;
}) => {
  return Object.entries(data).reduce((acc, [field, value]) => {
    if (
      value &&
      'kpiType' in value &&
      value.kpiType.includes(kpiPlanType) &&
      typeof value.presentDateRange.value.raw.value === 'number' &&
      value.presentDateRange.value.raw.suffix === '%'
    ) {
      acc.push({
        field: field,
        value: value.presentDateRange.value.raw.value,
        prefix: value.presentDateRange.value.raw.prefix || '',
        suffix: value.presentDateRange.value.raw.suffix || '',
        rawValue: value.presentDateRange.value.raw.value,
        previous: value.previousDateRange.filteredDataset[planPreviousDateRange].raw.value,
        benchmarkMin: value.presentDateRange.benchmark.raw.min,
        benchmarkMax: value.presentDateRange.benchmark.raw.max,
      });
    }

    return acc;
  }, []);
};

export const getActualRpKpis = ({
  data,
  planPreviousDateRange,
}: {
  data: DashboardData;
  planPreviousDateRange: PlanPreviousDateRangeDatasetKey;
}) => {
  const actualRpKpis: Array<KpisByPlanChartData> = [];

  RP_KPIS.map((kpi) => {
    const kpiData = data[kpi];

    if (kpiData) {
      actualRpKpis.push({
        field: kpi,
        value: kpiData.presentDateRange.value.raw.value,
        prefix: kpiData.presentDateRange.value.raw.prefix || '',
        suffix: kpiData.presentDateRange.value.raw.suffix || '',
        rawValue: kpiData.presentDateRange.value.raw.value,
        previous: kpiData.previousDateRange.filteredDataset[planPreviousDateRange].raw.value,
        benchmarkMin: kpiData.presentDateRange.benchmark.raw.min,
        benchmarkMax: kpiData.presentDateRange.benchmark.raw.max,
      });
    }
  });

  return actualRpKpis;
};

const Quarters = [
  'Q1: january-march',
  'Q2: april-june',
  'Q3: july-september',
  'Q4: october-december',
];

export const mapCYToFYPeriod = (cYear: string, cQuarter: number): Record<string, string> => {
  let fYear = parseInt(cYear);
  let fQuarter = cQuarter + 2;

  if (fQuarter > 3) {
    fQuarter -= 4;
    fYear += 1;
  } else if (fQuarter < 0) {
    fQuarter += 4;
    fYear -= 1;
  }

  const fiscalDate: Record<string, string> = {
    'fiscal Year': `FY${fYear.toString()}`,
  };

  if (cQuarter) {
    fiscalDate['fiscal Quarter'] = Quarters[fQuarter]?.split(':')?.[0];
  }

  return fiscalDate;
};

export const getPlanPreviousDateRangeCopy = (
  dateRange: DateRangeState,
  planPreviousDateRange: 'lastYear' | 'previousQuarter',
) => {
  if (planPreviousDateRange === 'lastYear') {
    return `CY${dateRange.year - 1}`;
  } else if (planPreviousDateRange === 'previousQuarter') {
    return dateRange.quarter === 1
      ? `CY${dateRange.year - 1} Q4`
      : `CY${dateRange.year} Q${dateRange.quarter - 1}`;
  }

  return '';
};
